import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import AnimeCard from "@/components/feature/AnimeCard";
import EmptyState from "@/components/base/EmptyState";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import { animes } from "@/mocks/anime";
import FilterPanel from "./components/FilterPanel";

const PAGE_SIZE = 10;

function viewsToNumber(views: string) {
  const value = parseFloat(views);
  if (Number.isNaN(value)) return 0;
  if (views.includes("亿")) return value * 10000;
  return value;
}

export default function Category() {
  useSeo({
    title: "分类 - 动漫资源 - 樱花动漫官网",
    description:
      "按类型、地区、年份与连载状态筛选动漫资源，支持热度、评分与更新时间排序，快速找到你想看的动画。",
    keywords: "动漫分类,动漫筛选,动漫资源,热血动漫,奇幻动漫,樱花动漫官网",
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);
  const { favorites } = useFavorites();

  const genresParam = searchParams.get("genre") || "";
  const selectedGenres = genresParam ? genresParam.split(",").filter(Boolean) : [];
  const region = searchParams.get("region") || "全部";
  const year = searchParams.get("year") || "全部";
  const status = searchParams.get("status") || "全部";
  const sort = searchParams.get("sort") || "最近更新";
  const onlyFav = searchParams.get("fav") === "1";

  const updateParams = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value) next.delete(key);
      else next.set(key, value);
    });
    setSearchParams(next);
  };

  const toggleGenre = (genre: string) => {
    const next = selectedGenres.includes(genre)
      ? selectedGenres.filter((item) => item !== genre)
      : [...selectedGenres, genre];
    updateParams({ genre: next.join(",") || null });
  };

  const resetAll = () => {
    setSearchParams(new URLSearchParams());
    setFilterOpen(false);
  };

  const filtered = useMemo(() => {
    const list = animes.filter((anime) => {
      if (selectedGenres.length > 0 && !selectedGenres.some((genre) => anime.genres.includes(genre))) {
        return false;
      }
      if (region !== "全部" && anime.region !== region) return false;
      if (year !== "全部" && String(anime.year) !== year) return false;
      if (status !== "全部" && anime.status !== status) return false;
      if (onlyFav && !favorites.includes(anime.id)) return false;
      return true;
    });

    return list.sort((a, b) => {
      if (sort === "热度最高") return viewsToNumber(b.views) - viewsToNumber(a.views);
      if (sort === "评分最高") return b.rating - a.rating;
      if (sort === "上映年份") return b.year - a.year;
      if (b.year !== a.year) return b.year - a.year;
      return b.currentEpisode - a.currentEpisode;
    });
  }, [selectedGenres, region, year, status, sort, onlyFav, favorites]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [genresParam, region, year, status, sort, onlyFav]);

  const visible = filtered.slice(0, visibleCount);
  const activeFilterCount =
    selectedGenres.length + (region !== "全部" ? 1 : 0) + (year !== "全部" ? 1 : 0) + (status !== "全部" ? 1 : 0);

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />
      <main className="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
        <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
          <span className="text-foreground-800">动漫分类</span>
          {selectedGenres.length > 0 && (
            <>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
              <span className="text-primary-600">{selectedGenres.join(" / ")}</span>
            </>
          )}
        </nav>

        <div className="mt-5 flex flex-col gap-4 border-b border-background-200 pb-6 md:flex-row md:items-end md:justify-between">
          <div>
            <span className="font-label text-[11px] uppercase tracking-[0.2em] text-primary-600">Anime library</span>
            <h1 className="mt-2 text-2xl font-bold text-foreground-950 md:text-3xl">动漫分类</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              从热血战斗到治愈日常，这里收录了两千余部作品的资料。用类型、地区、年份和连载状态组合筛选，找到今晚想看的下一部。
            </p>
          </div>
          <div className="flex items-center gap-4 text-sm">
            <span className="text-foreground-600">
              共 <strong className="font-heading text-lg text-primary-600">{filtered.length}</strong> 部作品
            </span>
            <button
              type="button"
              onClick={() => setFilterOpen((prev) => !prev)}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 lg:hidden"
            >
              <i className="ri-filter-3-line text-sm" />
              筛选
              {activeFilterCount > 0 && (
                <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-primary-500 px-1 font-label text-[10px] text-background-50">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>
        </div>

        <div className={`mt-6 ${filterOpen ? "block" : "hidden"} lg:block`}>
          <FilterPanel
            selectedGenres={selectedGenres}
            region={region}
            year={year}
            status={status}
            sort={sort}
            onlyFav={onlyFav}
            onToggleGenre={toggleGenre}
            onClearGenres={() => updateParams({ genre: null })}
            onSetRegion={(value) => updateParams({ region: value === "全部" ? null : value })}
            onSetYear={(value) => updateParams({ year: value === "全部" ? null : value })}
            onSetStatus={(value) => updateParams({ status: value === "全部" ? null : value })}
            onSetSort={(value) => updateParams({ sort: value === "最近更新" ? null : value })}
            onToggleFav={() => updateParams({ fav: onlyFav ? null : "1" })}
            onReset={resetAll}
          />
        </div>

        <div className="mt-7">
          {visible.length > 0 ? (
            <>
              <div className="grid grid-cols-2 gap-x-4 gap-y-7 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                {visible.map((anime) => (
                  <AnimeCard key={anime.id} anime={anime} />
                ))}
              </div>

              <div className="mt-10 flex flex-col items-center gap-3">
                <p className="text-xs text-foreground-500">
                  已显示 {visible.length} / {filtered.length} 部作品
                </p>
                {visible.length < filtered.length ? (
                  <button
                    type="button"
                    onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                    className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                  >
                    加载更多
                    <i className="ri-arrow-down-line text-sm" />
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 text-xs text-foreground-500">
                    <i className="ri-check-line" />
                    已经到底啦，换个筛选条件试试
                  </span>
                )}
              </div>
            </>
          ) : (
            <EmptyState
              title={onlyFav ? "收藏夹还是空的" : "暂无符合条件的作品"}
              description={
                onlyFav
                  ? "去动漫卡片右上角点一下爱心，收藏的作品会出现在这里，刷新页面也不会丢失。"
                  : "试试减少几个筛选条件，或者直接按「热血」「奇幻」这类大类重新浏览一遍。"
              }
              actionLabel={onlyFav ? "去浏览全部作品" : "重置筛选条件"}
              onAction={resetAll}
              secondaryLabel="回到首页"
              secondaryTo="/"
            />
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}